/**
 * Foxy — the Foxet expert's face: the fox mark (public/fox-icon.svg, ember), canvas-drawn on the same
 * useCanvasLoop and PresenterState machine as Mr. Clippy. Orange means the agent has the floor: her
 * glow is on only while she speaks (the AbstractPresenter rule). Foxy is the default mascot name;
 * tenants may rename theirs and a real capture carries that name.
 *
 * The mark is drawn at UNIFORM scale, fitted to the box by its own ink bounds: in the SVG's 720×720
 * viewBox the ink spans x 240–785 and y 152–872 (the ears reach y 152, the diamond's bottom vertex
 * y 872), so the mark is 545 wide × 720 tall (0.757). A non-square box letterboxes it; it is never
 * stretched.
 *
 * Face: Foxy's eyes are the SAME drawEyes() as Mr. Clippy's (one code path, no fox-specific eye code),
 * centred on the face mass at 0.55 of the ink height from the ink's top, spaced ±0.19 of the ink width,
 * radius 0.103 of the ink width (7px at an 88px-tall mark; see the comment at the call for the numbers), white iris with a 1px ink outline, a 46%
 * pupil, a blink that closes to an ember line with the ink outline. `face="none"` draws the plain logo
 * mark (the sections' own marks outside the ledger have no face).
 *
 * States: speaking = ember glow + bob on the synthetic level; thinking = lean with the three orbiting
 * dots; listening = breathing (3% peak to peak) and an ear twitch every ~6s (±6° for 120ms).
 */
import { useRef } from 'react';
import { useCanvasLoop } from './useCanvasLoop';
import { EyeRig, drawEyes, type Eyes } from './eyes';
import type { Persona, PresenterState } from './CharacterPresenter';

const EMBER = '#E8530A';
const EMBER_DIM = 'rgba(232,83,10,0.55)';

// The three subpaths of fox-icon.svg (left ear, right ear, head), in its path space
// (viewBox coords = path × 0.1, y flipped about 1024).
const LEFT_EAR = 'M2640 7000 c0 -1015 4 -1720 9 -1718 5 2 247 235 537 518 291 283 688 670 882 859 194 189 351 347 350 352 -2 4 -102 103 -223 220 -121 117 -281 272 -355 344 -74 72 -238 230 -365 350 -126 120 -345 330 -485 465 -140 136 -276 266 -302 290 l-48 43 0 -1723z';
const RIGHT_EAR = 'M7434 8568 c-82 -78 -214 -205 -294 -283 -80 -77 -260 -251 -400 -386 -140 -135 -269 -258 -285 -275 -181 -175 -550 -530 -587 -563 -27 -24 -48 -47 -48 -51 0 -3 307 -305 683 -671 375 -365 772 -753 883 -861 110 -109 203 -198 207 -198 4 0 7 772 7 1715 0 955 -4 1715 -9 1715 -4 0 -75 -64 -157 -142z';
const HEAD = 'M3756 5611 c-747 -747 -1357 -1363 -1355 -1369 2 -7 615 -623 1361 -1370 l1358 -1357 1365 1365 1365 1365 -1362 1362 c-750 749 -1365 1362 -1368 1362 -3 0 -617 -611 -1364 -1358z';
/** Ink bounds in viewBox units. */
export const INK = { x0: 240, x1: 785, y0: 152, y1: 872, w: 545, h: 720 } as const;
/** Ear pivots (viewBox units): where each ear meets the head. */
const PIVOT_L = { x: 330, y: 470 };
const PIVOT_R = { x: 695, y: 470 };

export function Foxy({ state, levelRef, size, eyes, persona, facing, face = 'eyes' }: {
  state: PresenterState;
  levelRef: React.RefObject<number>;
  size: number;
  eyes?: Eyes;
  persona?: Persona;
  facing?: 'left' | 'right';
  face?: 'eyes' | 'none';
}) {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const paths = useRef<{ l: Path2D; r: Path2D; h: Path2D } | null>(null);
  const rig = useRef(new EyeRig());
  const mem = useRef({ twitchAt: -1, nextTwitch: 4 });
  const canvasRef = useCanvasLoop(({ ctx, w, h, t, reduced }) => {
    if (!paths.current) paths.current = { l: new Path2D(LEFT_EAR), r: new Path2D(RIGHT_EAR), h: new Path2D(HEAD) };
    const P = paths.current;
    const M = mem.current;
    const now = performance.now();
    const cx = w / 2;
    const cy = h / 2;
    const level = state === 'speaking' ? (levelRef.current ?? 0) : 0;
    const speaking = state === 'speaking';
    const grey = state === 'muted' || state === 'offline';
    const fill = grey ? EMBER_DIM : EMBER;
    const breath = persona?.breath ?? 3;
    // uniform scale: the ink's height fits 94% of the box (letterboxed when the box is not square)
    const k = (Math.min(w, h) * 0.94) / INK.h;
    const W = INK.w * k;
    const H = INK.h * k;

    let tilt = 0;
    let bob = 0;
    let scale = 1;
    if (reduced) {
      tilt = state === 'thinking' ? -0.16 : 0;
    } else if (speaking) {
      bob = -level * H * 0.03;
      tilt = Math.sin(t * 9) * 0.04 * level;
    } else if (state === 'thinking') {
      tilt = -0.16 + Math.sin(t * 2) * 0.02;
    } else {
      scale = 1 + 0.015 * Math.sin((t / breath) * Math.PI * 2); // breathing, 3% peak to peak about rest
    }
    if (!reduced && state === 'listening' && t > M.nextTwitch && M.twitchAt < 0) {
      M.twitchAt = t;
      M.nextTwitch = t + 5 + ((Math.sin(t * 7.13) + 1) / 2) * 2;
    }
    const twitching = M.twitchAt >= 0 && t - M.twitchAt < 0.12;
    if (M.twitchAt >= 0 && !twitching) M.twitchAt = -1;
    const twitch = twitching ? ((6 * Math.PI) / 180) * Math.sin(((t - M.twitchAt) / 0.12) * Math.PI) : 0;
    const frame = rig.current.update(face === 'eyes' ? eyes : undefined, 0, t, now, reduced);

    ctx.save();
    ctx.translate(cx, cy + bob);
    ctx.rotate(tilt + ((persona?.lean ?? 0) * Math.PI) / 180);
    if (facing === 'right') ctx.scale(-1, 1);
    ctx.scale(scale, scale);
    if (speaking && level > 0.03) {
      ctx.shadowColor = `rgba(232,83,10,${0.55 * level})`;
      ctx.shadowBlur = 22 * level;
    }
    // viewBox space with the ink centred: one unit = k CSS px
    ctx.scale(k, k);
    ctx.translate(-(INK.x0 + INK.w / 2), -(INK.y0 + INK.h / 2));
    const drawPath = (p: Path2D) => {
      ctx.save();
      ctx.translate(0, 1024);
      ctx.scale(0.1, -0.1);
      ctx.fill(p);
      ctx.restore();
    };
    ctx.fillStyle = fill;
    drawPath(P.h);
    for (const [ear, pivot, sign] of [[P.l, PIVOT_L, 1], [P.r, PIVOT_R, -1]] as const) {
      ctx.save();
      ctx.translate(pivot.x, pivot.y);
      ctx.rotate(twitch * sign);
      ctx.translate(-pivot.x, -pivot.y);
      drawPath(ear);
      ctx.restore();
    }
    ctx.shadowBlur = 0;
    const px = 1 / k; // one CSS pixel in viewBox units
    if (face === 'eyes') {
      const cxv = INK.x0 + INK.w / 2;
      drawEyes(ctx, {
        // The brief: 0.50 H, ±0.20 W, r 0.085 W "≈ 7px at 88px tall" — written for a square mark. This mark's
        // ink is 545×720 and its face mass (the diamond) is centred at 0.62 H, so the three numbers are moved
        // together to keep the intent (≈ 7px eyes inside the face, whites clear of the edge on every row):
        // 0.55 H, ±0.19 W, r 0.103 W = 7.0px at 88px tall; pupil travel 0.20 r so the pupil never touches the outline.
        xs: [cxv - 0.19 * INK.w, cxv + 0.19 * INK.w], y: INK.y0 + 0.55 * INK.h, r: 0.103 * INK.w, px, lidColor: fill, mirrored: facing === 'right',
        lookUp: state === 'thinking' ? -0.08 * 0.103 * INK.w : 0, dim: grey, pupil: 0.46, travel: 0.2,
      }, frame);
      ctx.canvas.dataset.pupil = `${Math.abs(frame.px).toFixed(2)},${Math.abs(frame.py).toFixed(2)}`;
    }
    ctx.restore();

    if (state === 'thinking') {
      for (let j = 0; j < 3; j++) {
        const p = reduced ? j * 0.33 : ((t * 0.7 + j * 0.28) % 1);
        const ang = -Math.PI / 2 + (p - 0.5) * 1.5;
        const rr = W * 0.42;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(ang) * rr, cy - H * 0.3 + Math.sin(ang) * rr * 0.35, Math.min(w, h) * 0.011, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232,83,10,${0.35 + 0.5 * Math.sin(p * Math.PI)})`;
        ctx.fill();
      }
    }
  }, [state, face, reduced ? `${eyes?.mode}/${eyes?.key}/${facing}` : '']);
  return <canvas ref={canvasRef} width={size} height={size} style={{ width: size, height: size, display: 'block' }} aria-hidden="true" data-face={face} />;
}
